import { useState } from "react";

import { useMount } from "@/Common/CustomHooks/useMount";
import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { Patient } from "@/Common/Models/Patient/Patient";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models//DirectoryHealthCareProvider";
import { DirectoryPatientLinksHelper } from "../Helpers/DirectoryPatientLinksHelper";
import { PatientContactDirectoryHelper } from "../Helpers/PatientContactDirectoryHelper";
import { PharmacyProxy } from "../Models/PharmacyProxy";

interface PatientPreferredContacts {
    externalConsultants: DirectoryHealthCareProvider[],
    preferredPharmacy: PharmacyProxy | null
}

function isEqualPreferredPharmacy(p1: PharmacyProxy, p2: PharmacyProxy): boolean {
    const pharmacy1 = p1?.getPharmacyInfo();
    const pharmacy2 = p2?.getPharmacyInfo();

    if (p1 === null && p2 === null)
        return true;

    return !!p1
        && !!p2
        && pharmacy1.Address === pharmacy2.Address
        && pharmacy1.Banner === pharmacy2.Banner
        && pharmacy1.Fax === pharmacy2.Fax
        && pharmacy1.Phone === pharmacy2.Phone
        && pharmacy1.Name === pharmacy2.Name;
}

function areExternalConsultantsEqual(externalConsultants1: DirectoryHealthCareProvider[], externalConsultants2: DirectoryHealthCareProvider[]): boolean {
    if (externalConsultants1.length !== externalConsultants2.length)
        return false;

    if (externalConsultants1.any() && externalConsultants2.any())
        return true;

    const e2Dictionary = externalConsultants2.toDictionary(p => p.id);

    for (let e1 of externalConsultants1) {
        const e2 = e2Dictionary[e1.id];
        if (!e2)
            return false;

        if (!DirectoryHealthCareProvider.isEqual(e1, e2))
            return false;
    }
    return true;
}
/**
 * Hook that exposes the patient's default contacts (preferred pharmacy, external consultants)
 * @param patientContactId The patient contact id
 * @param patient Loaded patient model
 * @returns @type PatientPreferredContacts
 */

function useDefaultFaxDirectoryContacts(patientContactId: string | null, patient?: Patient): PatientPreferredContacts {
    const [defaultContacts, setDefaultContacts] = useState({ preferredPharmacy: null, externalConsultants: [] });

    async function getDefaultContacts(): Promise<void> {
        if (!patientContactId && !patient)
            return;

        let contacts: PatientPreferredContacts;

        if (patient) {
            contacts = DirectoryPatientLinksHelper.getFromPatientModel(patient);
        }
        else if (patientStore.PatientModels?.[patientContactId]) {
            contacts = {
                externalConsultants: DirectoryPatientLinksHelper.getExternalConsultants(patientContactId),
                preferredPharmacy: DirectoryPatientLinksHelper.getPreferredPharmacy(patientContactId)
            };
        }
        else if (patientContactId) {
            const loadedPatientModel = await PatientContactDirectoryHelper.loadPatientPreferredContacts(patientContactId);

            if (!loadedPatientModel)
                return;

            contacts = {
                preferredPharmacy: loadedPatientModel.preferredPharmacy,
                externalConsultants: loadedPatientModel.externalConsultants
            };
        }

        if (contacts
            && isEqualPreferredPharmacy(contacts.preferredPharmacy, defaultContacts.preferredPharmacy)
            && areExternalConsultantsEqual(defaultContacts.externalConsultants, contacts.externalConsultants)
        )
            return;

        setDefaultContacts(contacts);
    }

    useMount(() => getDefaultContacts());
    useOnStoreChange(
        patientStore,
        () => {
            getDefaultContacts();
        }
    );

    return defaultContacts;
}

export { useDefaultFaxDirectoryContacts };
